// Rental Management Types for Frontend
import { Customer } from './customer';

// These types are defined locally to avoid circular dependencies
// Previously imported from './item' but moved here to fix compilation issues
export interface SerialNumber {
  id: string;
  serialNumber: string;
  productId: string;
  warehouseId?: string;
  status: string;
  product?: {
    id: string;
    name: string;
    sku?: string;
  };
  warehouse?: {
    id: string;
    name: string;
  };
}

export interface Product {
  id: string;
  name: string;
  sku: string;
  itemType: string;
  unitPrice: number;
  costPrice: number;
  isSerialized: boolean;
}

// Enums
export enum RentalContractStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  EXPIRED = 'EXPIRED',
  TERMINATED = 'TERMINATED',
  RENEWED = 'RENEWED'
}

export enum RentalMachineStatus {
  RENTED = 'RENTED',
  RETURNED = 'RETURNED',
  MAINTENANCE = 'MAINTENANCE'
}

export enum RefillScheduleType {
  AUTO = 'AUTO',
  ON_DEMAND = 'ON_DEMAND'
}

export enum MachineCondition {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR',
  DAMAGED = 'DAMAGED'
}

export enum VisitType {
  REFILL = 'REFILL',
  SERVICE = 'SERVICE',
  SCENT_CHANGE = 'SCENT_CHANGE',
  SAMPLE_DELIVERY = 'SAMPLE_DELIVERY'
}

export enum VisitPriority {
  NORMAL = 'NORMAL',
  URGENT = 'URGENT'
}

export enum VisitStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  NEEDS_FOLLOWUP = 'NEEDS_FOLLOWUP'
}

// Base interfaces
export interface RentalMachine {
  id: string;
  contractId: string;
  serialNumberId: string;
  monthlyRentalFee: number;
  status: RentalMachineStatus;
  returnDate?: Date;
  returnCondition?: MachineCondition;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  serialNumber?: SerialNumber;
}

export interface RefillSchedule {
  id: string;
  contractId: string;
  machineId: string;
  scheduleType: RefillScheduleType;
  lastRefillDate?: Date;
  nextRefillDate: Date;
  intervalDays: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface RefillVisitDetail {
  id: string;
  visitId: string;
  machineId: string;
  oilProductId: string;
  oilScentName: string;
  amountML: number;
  costPerML: number;
  totalCost: number;
  notes?: string;
  machine?: RentalMachine;
  oilProduct?: Product;
}

export interface RefillVisit {
  id: string;
  visitNumber: string;
  companyId: string;
  contractId: string;
  customerId: string;
  visitDate: Date;
  employeeId: string;
  totalOilUsed: number;
  totalCost: number;
  notes?: string;
  visitType: VisitType;
  priority: VisitPriority;
  requestedByCustomer: boolean;
  requestNotes?: string;
  fromScent?: string;
  toScent?: string;
  visitStatus: VisitStatus;
  issueResolved?: boolean;
  followupRequired: boolean;
  completionNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  employee?: {
    id: string;
    name: string;
  };
  customer?: {
    id: string;
    name: string;
    phone?: string;
    address?: string;
  };
  contract?: {
    id: string;
    contractNumber: string;
  };
  company?: {
    id: string;
    name: string;
  };
  machines?: {
    id: string;
    serialNumber: string;
    productName: string;
  }[];
  details?: RefillVisitDetail[];
}

export interface OilScentPreference {
  id: string;
  contractId: string;
  customerId: string;
  scentName: string;
  productId: string;
  preferenceDate: Date;
  notes?: string;
}

export interface OilInventoryTracking {
  id: string;
  companyId: string;
  productId: string;
  warehouseId: string;
  totalBottles: number;
  openBottleML: number;
  totalMLUsed: number;
  lastRefillDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  product?: Product;
}

export interface RentalContract {
  id: string;
  contractNumber: string;
  companyId: string;
  customerId: string;
  startDate: Date;
  endDate: Date;
  monthlyRentalFee: number;
  refillServiceFee: number;
  status: RentalContractStatus;
  contractDocument?: string;
  notes?: string;
  autoRenew: boolean;
  refillIntervalDays: number;
  createdAt: Date;
  updatedAt: Date;
  customer?: Customer;
  machines?: RentalMachine[];
  refillSchedules?: RefillSchedule[];
  refillVisits?: RefillVisit[];
  scentPreferences?: OilScentPreference[];
}

// Create/Update DTOs
export interface CreateRentalContractData {
  customerId: string;
  startDate: Date | string;
  endDate: Date | string;
  monthlyRentalFee: number;
  refillServiceFee?: number;
  contractDocument?: string;
  notes?: string;
  autoRenew?: boolean;
  refillIntervalDays?: number;
  machines: {
    serialNumberId: string;
    monthlyRentalFee?: number;
    // Bottle tracking
    initialBottlesQty?: number;
    initialBottlesMl?: number;
    bottlesProductId?: string;
  }[];
}

export interface UpdateRentalContractData {
  startDate?: Date | string;
  endDate?: Date | string;
  monthlyRentalFee?: number;
  refillServiceFee?: number;
  contractDocument?: string;
  notes?: string;
  autoRenew?: boolean;
  refillIntervalDays?: number;
  status?: RentalContractStatus;
}

export interface RefillDetailData {
  machineId: string;
  oilProductId: string;
  oilScentName: string;
  amountML: number;
  notes?: string;
}

export interface CreateRefillVisitData {
  contractId: string;
  visitDate?: Date | string;
  employeeId: string;
  notes?: string;
  details: RefillDetailData[];
}

export interface UpdateRefillVisitData {
  visitDate?: Date | string;
  employeeId?: string;
  notes?: string;
}

export interface ReturnMachinesData {
  machines: {
    machineId: string;
    returnCondition: MachineCondition;
    notes?: string;
  }[];
}

// Selective Return with Replacement Types
export interface ReplacementMachineData {
  serialNumberId: string;
  monthlyRentalFee?: number;
  initialBottlesQty?: number;
  initialBottlesMl?: number;
  bottlesProductId?: string;
}

export interface MachineReturnItem {
  machineId: string;
  returnCondition: MachineCondition;
  notes?: string;
  replacement?: ReplacementMachineData;
}

export interface SelectiveReturnData {
  returns: MachineReturnItem[];
  returnDate: Date | string;
  setContractStatus?: RentalContractStatus;
}

export interface AvailableMachineForReplacement {
  id: string;
  serialNumber: string;
  productId: string;
  productName: string;
  productSku: string;
  warehouseId: string | null;
  warehouseName?: string;
  condition: string;
}

export interface CreateVisitScheduleData {
  contractId: string;
  machineIds: string[];
  visitType: VisitType;
  priority: VisitPriority;
  scheduledDate: Date | string;
  requestNotes?: string;
  fromScent?: string;
  toScent?: string;
}

export interface UpdateVisitStatusData {
  visitStatus: VisitStatus;
  issueResolved?: boolean;
  followupRequired?: boolean;
  completionNotes?: string;
}

// Query interfaces
export interface GetRentalContractsQuery {
  page?: number;
  limit?: number;
  status?: RentalContractStatus;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface GetRefillVisitsQuery {
  page?: number;
  limit?: number;
  contractId?: string;
  startDate?: string;
  endDate?: string;
  employeeId?: string;
}

export interface GetMachinesNeedingRefillQuery {
  daysAhead?: number;
  customerId?: string;
  status?: 'due' | 'overdue' | 'all';
}

// Response interfaces
export interface RentalContractResponse {
  success: boolean;
  data: RentalContract;
  message?: string;
}

export interface RentalContractsResponse {
  success: boolean;
  data: RentalContract[];
  total: number;
  page: number;
  limit: number;
  message?: string;
}

export interface RefillVisitResponse {
  success: boolean;
  data: RefillVisit;
  message?: string;
}

export interface RefillVisitsResponse {
  success: boolean;
  data: RefillVisit[];
  total: number;
  page: number;
  limit: number;
  message?: string;
}

export interface MachineNeedingRefill {
  machine: RentalMachine;
  contract: RentalContract;
  schedule: RefillSchedule;
  daysUntilDue: number;
  isOverdue: boolean;
}

export interface MachinesNeedingRefillResponse {
  success: boolean;
  data: MachineNeedingRefill[];
  message?: string;
}

export interface ScheduledVisitResponse {
  success: boolean;
  data: RefillVisit;
  message?: string;
}

export interface UpcomingVisitsResponse {
  success: boolean;
  data: RefillVisit[];
  count: number;
  message?: string;
}

export interface VisitsThisWeekResponse {
  success: boolean;
  data: { count: number };
  message?: string;
}

// Dashboard interfaces
export interface RentalDashboardStats {
  totalContracts: number;
  activeContracts: number;
  expiringCount: number;
  totalMachinesRented: number;
  availableMachines: number;
  refillsDueThisWeek: number;
  refillsOverdue?: number;
  visitsThisWeek?: number;
  monthlyRentalRevenue: number;
  oilUsedThisMonth: number;
}

export interface RentedMachineInfo {
  machine: RentalMachine;
  contract: RentalContract;
  customer: Customer;
  serialNumber: SerialNumber;
}

export interface ExpiringContract {
  contract: RentalContract;
  customer: Customer;
  daysUntilExpiry: number;
  machineCount: number;
}

export interface OilInventoryStatus {
  product: Product;
  tracking: OilInventoryTracking;
  bottlesRemaining: number;
  mlRemaining: number;
  estimatedDaysRemaining: number;
}

export interface RevenueReportData {
  month: string;
  rentalRevenue: number;
  refillRevenue: number;
  totalRevenue: number;
  oilCost: number;
  profit: number;
  contractCount: number;
  refillCount: number;
}

export interface RevenueReportResponse {
  success: boolean;
  data: RevenueReportData[];
  message?: string;
}
